import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { useCallback, useContext } from 'react';

import { TimesyncContext } from '../providers/TimesyncProvider/TimesyncProvider';
import BackendService from '../services/backendService';
import { Game, GameEditOrCreateDTO } from '../services/backendService/types';
import { Template } from '../services/TemplateService/Template.type';
import useAutosaveTemplate from './useAutosaveTemplate';
import useCreateGameFromTemplate from './useCreateGameFromTemplate';
import { gameCacheKey } from './useGame';
import { NewRelic, newRelicNoticeError } from './useNewRelic';
import useRouterMatch from './useRouterMatch';

function useGameAutoSave() {
  const client = useQueryClient();
  const { tenantId } = useRouterMatch();
  const { autoSaveTemplate } = useAutosaveTemplate();
  const { now } = useContext(TimesyncContext);

  const {
    createGameFromTemplate: createGameFromTemplatePresent,
    isCreatingGameFromTemplate,
    isCreatingGameFromTemplateSuccess,
  } = useCreateGameFromTemplate();

  const mutateGame = useCallback(
    async (mutateBody: GameEditOrCreateDTO) => {
      const cacheKey = [gameCacheKey, mutateBody?.gameId];
      const previousGame = client.getQueryData<Game>(cacheKey);
      client.setQueryData<Game | undefined>(cacheKey, game => {
        if (!game) return game;
        return {
          ...game,
          slides: mutateBody.slides === undefined ? game.slides : mutateBody.slides,
          title: mutateBody.title === undefined ? game.title : mutateBody.title,
          topicId: mutateBody.topicId === undefined ? game.topicId : mutateBody.topicId,
          lastPlayedAt:
            mutateBody.lastPlayedAt === undefined
              ? game.lastPlayedAt || null
              : mutateBody.lastPlayedAt,
          currentRound:
            mutateBody.currentRound === undefined
              ? game.currentRound
              : mutateBody.currentRound,
          roundStartsAt:
            mutateBody.roundStartsAt === undefined
              ? game.roundStartsAt || null
              : mutateBody.roundStartsAt,
          isPlaying:
            mutateBody.isPlaying === undefined
              ? game.isPlaying || false
              : mutateBody.isPlaying,
          requireRealNames:
            mutateBody.requireRealNames === undefined
              ? game.requireRealNames
              : mutateBody.requireRealNames,
          isShowingLobby:
            mutateBody.isShowingLobby === undefined
              ? game.isShowingLobby
              : mutateBody.isShowingLobby,
        };
      });

      // Return the snapshotted value
      return () => client.setQueryData(cacheKey, previousGame);
    },
    [client],
  );
  const { mutate, isLoading, isError, isSuccess, isIdle } = useMutation<
    Game,
    AxiosError,
    GameEditOrCreateDTO & { onSuccessFunction?: (gameId: string) => void }
  >(
    gameConfig => {
      return BackendService.instance.game.autoSave({
        ...gameConfig,
        modifiedAt: now(),
      });
    },
    {
      onSuccess: (data, { onSuccessFunction }) => {
        client.setQueryData([gameCacheKey, data.id, 'success'], data);
        if (onSuccessFunction) {
          onSuccessFunction(data.id);
        }
      },
      onMutate: async mutateBody => {
        const cacheKey = [gameCacheKey, mutateBody?.gameId, 'success'];
        const previousSuccessGame = client.getQueryData<Game>(cacheKey);
        return () => client.setQueryData(cacheKey, previousSuccessGame);
      },
      retryDelay: 2000,
      retry: (count: number, error) => {
        // due to SSR fetching we dont want to retry on 403s and 404s
        if (error.response?.status && [403, 404].includes(error.response?.status)) {
          return false;
        }

        // Default retry is 3 times
        if (count > 3) {
          return false;
        }
        return true;
      },
      onError: (err, bodyWithError, rollback) => {
        newRelicNoticeError(err, 'Error while auto saving game');

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        return rollback();
      },
    },
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAutoSave = useCallback(
    debounce(
      async (
        draft: GameEditOrCreateDTO,
        server: GameEditOrCreateDTO,
        onSuccessFunction?: (gameId: string) => void,
      ) => {
        if (JSON.stringify(draft) !== JSON.stringify(server)) {
          mutate({ ...draft, onSuccessFunction });
        }
      },
      500,
    ),
    [],
  );
  const autoSave = useCallback(
    async (
      gameEditDTO: GameEditOrCreateDTO,
      onSuccessFunction?: (gameId: string) => void,
      template?: Template,
    ) => {
      const gameId = gameEditDTO.gameId || undefined;
      const key = [gameCacheKey, gameId];
      const serverGameCache = client.getQueryData<Game | undefined>(key);

      if (serverGameCache) {
        // to prevent race condition
        if (
          gameEditDTO.modifiedAt &&
          serverGameCache.modifiedAt &&
          gameEditDTO.modifiedAt < serverGameCache.modifiedAt
        )
          return;
        const serverGameDTO: GameEditOrCreateDTO = {
          ...gameEditDTO,
          slides: serverGameCache.slides || [],
          currentRound: serverGameCache.currentRound,
          roundStartsAt: serverGameCache.roundStartsAt,
          lastPlayedAt: serverGameCache.lastPlayedAt,
          isPlaying: serverGameCache.isPlaying,
          requireRealNames: serverGameCache.requireRealNames,
        };
        await mutateGame(gameEditDTO);
        debouncedAutoSave(gameEditDTO, serverGameDTO, onSuccessFunction);
        return;
      }
      if (gameId) {
        const serverGame = await BackendService.instance.game.retrieve(gameId);
        if (serverGame) {
          // to prevent race condition when creating
          if (
            gameEditDTO.modifiedAt &&
            serverGame.modifiedAt &&
            gameEditDTO.modifiedAt < serverGame.modifiedAt
          )
            return;
          client.setQueryData(key, serverGame);
          const serverGameDTO: GameEditOrCreateDTO = {
            ...gameEditDTO,
            slides: serverGame.slides || [],
            title: serverGame.title,
            currentRound: serverGame.currentRound,
            roundStartsAt: serverGame.roundStartsAt,
            lastPlayedAt: serverGame.lastPlayedAt,
            isPlaying: serverGame.isPlaying,
            requireRealNames: serverGame.requireRealNames,
          };
          await mutateGame(gameEditDTO);
          debouncedAutoSave(gameEditDTO, serverGameDTO, onSuccessFunction);
          return;
        }
      }

      if (
        template &&
        !template.content.activeGame?.id &&
        !isCreatingGameFromTemplate &&
        !isCreatingGameFromTemplateSuccess
      ) {
        // const rounds = gameEditDTO.rounds || [];
        const slides = gameEditDTO.slides || [];
        const roundToStartFrom =
          !!slides && slides.length > 0
            ? gameEditDTO.currentRound
              ? gameEditDTO.currentRound
              : slides[0].id
            : null;
        createGameFromTemplatePresent({
          activeTenantId: tenantId,
          isFromDiscover: false,
          templateConfig: {
            slides,
            id: template.id,
            metadata: template.metadata,
            requireRealNames: gameEditDTO.requireRealNames,
            currentRound: roundToStartFrom,
            modifiedAt: new Date().getTime(),
          },
          onSuccessFunction: (gameId: string) => {
            autoSaveTemplate({
              templateConfig: {
                id: template.id,
                modifiedAt: gameEditDTO.modifiedAt || new Date().getTime(),
                activeGame: {
                  isOngoing: false,
                  id: gameId,
                },
              },
            });
          },
        });
      }
    },
    [
      autoSaveTemplate,
      client,
      createGameFromTemplatePresent,
      debouncedAutoSave,
      isCreatingGameFromTemplate,
      isCreatingGameFromTemplateSuccess,
      mutateGame,
      tenantId,
    ],
  );
  return {
    autoSave,
    isLoading,
    isError,
    isSuccess,
    isIdle,
  };
}

export default useGameAutoSave;
